import { useState, useCallback, useMemo } from 'react';
import { BookOpen, ChevronLeft, ChevronRight, RotateCcw, Undo2, Paperclip } from 'lucide-react';
import {
  BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Legend, ResponsiveContainer,
  Tooltip as ChartTooltip,
} from 'recharts';
import { analyticsApi } from '@/api/analytics.api';
import { usePermission } from '@/hooks/usePermission';
import { useAuth } from '@/hooks/useAuth';
import { Permissions } from '@/constants/permissions';
import { Spinner } from '@/components/ui/Spinner';
import { cn } from '@/utils/cn';
import { ReportToolbar, RunReportButton } from '@/components/reports/ReportToolbar';
import { SavedViewBar } from '@/components/reports/SavedViewBar';
import { LocationSelect } from '@/components/reports/LocationSelect';
import { CostCentreSelect } from '@/components/reports/CostCentreSelect';
import { ReportPrintPreview } from '@/components/reports/ReportPrintPreview';
import { ReportHeader } from '@/components/master/report/ReportHeader';
import { ReportFooter } from '@/components/master/report/ReportFooter';
import { exportToExcel, exportToCsvRows } from '@/utils/reportExport';
import type { ExcelColumn } from '@/utils/reportExport';
import {
  INPUT_CLS, LABEL_CLS, TH_CLS, TH_R_CLS, AMT_CLS, CARD_CLS,
  today, firstOfMonth, fmtDate, fmtAmt, fmtAmtZero,
  Section, EmptyState, CHART_COLOURS,
} from '@/components/analytics/AnalyticsKit';
import type {
  JournalAnalysisDto, JournalAnalysisFilter, JournalAnalysisRow,
} from '@/types/analytics.types';

// ── Options ───────────────────────────────────────────────────────────────────

/** Values are the backend enum member names — the API parses them case-insensitively. */
const WORKFLOW_STATUS_OPTIONS = [
  { value: '',                label: 'All Statuses' },
  { value: 'Draft',           label: 'Draft' },
  { value: 'Submitted',       label: 'Submitted' },
  { value: 'PendingApproval', label: 'Pending Approval' },
  { value: 'Approved',        label: 'Approved' },
  { value: 'Posted',          label: 'Posted' },
  { value: 'Rejected',        label: 'Rejected' },
  { value: 'Cancelled',       label: 'Cancelled' },
];

const POSTING_STATUS_OPTIONS = [
  { value: '',          label: 'All' },
  { value: 'Posted',    label: 'Posted' },
  { value: 'NotPosted', label: 'Not Posted' },
];

const SOURCE_OPTIONS: { value: '' | 'MANUAL' | 'AUTOMATIC'; label: string }[] = [
  { value: '',          label: 'All Sources' },
  { value: 'MANUAL',    label: 'Manual' },
  { value: 'AUTOMATIC', label: 'Automatic' },
];

const DEFAULT_PAGE_SIZE = 100;
const TOP_N = 10;

/** Quick-view presets shown above the filter panel. */
type QuickView =
  | 'ALL' | 'UNPOSTED' | 'MANUAL' | 'AUTOMATED'
  | 'REVERSED' | 'BACKDATED' | 'CLOSED_PERIOD';

const QUICK_VIEWS: { key: QuickView; label: string }[] = [
  { key: 'ALL',           label: 'All' },
  { key: 'UNPOSTED',      label: 'Unposted' },
  { key: 'MANUAL',        label: 'Manual' },
  { key: 'AUTOMATED',     label: 'Automated' },
  { key: 'REVERSED',      label: 'Reversed' },
  { key: 'BACKDATED',     label: 'Backdated' },
  { key: 'CLOSED_PERIOD', label: 'Closed-period' },
];

function fmtDateTime(iso: string | null): string {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    return `${d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })} ${d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}`;
  } catch {
    return iso ?? '—';
  }
}

// ── Status pill ───────────────────────────────────────────────────────────────

const WORKFLOW_TONES: Record<string, string> = {
  Draft:             'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300',
  Submitted:         'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400',
  PendingApproval:   'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400',
  PartiallyApproved: 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400',
  Approved:          'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400',
  Posted:            'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400',
  Rejected:          'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400',
  Cancelled:         'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400',
  Escalated:         'bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-400',
  NotPosted:         'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300',
  Failed:            'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400',
};

const StatusBadge = ({ status }: { status: string }) => (
  <span
    className={cn(
      'inline-block px-1.5 py-0.5 rounded text-[10px] font-medium whitespace-nowrap',
      WORKFLOW_TONES[status] ?? 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300',
    )}
  >
    {status}
  </span>
);

// ── Summary tile ──────────────────────────────────────────────────────────────

const SummaryTile = ({ label, value, danger }: {
  label: string;
  value: string;
  /** An unbalanced journal is a defect, not a statistic — it gets shouted about. */
  danger?: boolean;
}) => (
  <div
    className={cn(
      CARD_CLS, 'p-3 flex flex-col gap-1',
      danger && 'border-red-300 dark:border-red-800 bg-red-50 dark:bg-red-900/20',
    )}
  >
    <span className="text-xs font-medium text-gray-500 dark:text-gray-400">{label}</span>
    <span
      className={cn(
        'text-xl font-bold tabular-nums',
        danger ? 'text-red-600 dark:text-red-400' : 'text-gray-900 dark:text-white',
      )}
    >
      {value}
    </span>
  </div>
);

// ── Chart tooltips ────────────────────────────────────────────────────────────

const CountTooltip = ({ active, payload, label }: {
  active?: boolean;
  payload?: Array<{ value: number }>;
  label?: string;
}) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg px-3 py-2 text-xs">
      <p className="font-semibold text-gray-700 dark:text-gray-200">{label}</p>
      <p className="text-gray-900 dark:text-white font-medium tabular-nums mt-0.5">
        {payload[0].value.toLocaleString('en-US')} journals
      </p>
    </div>
  );
};

const SourceTooltip = ({ active, payload }: {
  active?: boolean;
  payload?: Array<{ name: string; value: number; payload: { fill: string } }>;
}) => {
  if (!active || !payload?.length) return null;
  const p = payload[0];
  return (
    <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg px-3 py-2 text-xs">
      <span className="flex items-center gap-1.5 font-medium" style={{ color: p.payload.fill }}>
        <span className="w-2 h-2 rounded-full inline-block" style={{ backgroundColor: p.payload.fill }} />
        {p.name}
      </span>
      <span className="block text-gray-900 dark:text-white font-semibold mt-0.5">
        {p.value.toLocaleString('en-US')} journals
      </span>
    </div>
  );
};

// ── Excel export ──────────────────────────────────────────────────────────────

const EXCEL_COLUMNS: ExcelColumn[] = [
  { key: 'JournalNo',      header: 'Journal No',      width: 18 },
  { key: 'DocDate',        header: 'Doc Date',        width: 14 },
  { key: 'PostingDate',    header: 'Posting Date',    width: 14 },
  { key: 'Period',         header: 'Period',          width: 12 },
  { key: 'Type',           header: 'Type',            width: 14 },
  { key: 'Reference',      header: 'Reference',       width: 20 },
  { key: 'Narration',      header: 'Narration',       width: 36 },
  { key: 'WorkflowStatus', header: 'Workflow Status', width: 16 },
  { key: 'PostingStatus',  header: 'Posting Status',  width: 14 },
  { key: 'Debit',          header: 'Debit',           width: 16, numFmt: '#,##0.00' },
  { key: 'Credit',         header: 'Credit',          width: 16, numFmt: '#,##0.00' },
  { key: 'Difference',     header: 'Difference',      width: 14, numFmt: '#,##0.00' },
  { key: 'Lines',          header: 'Lines',           width: 8 },
  { key: 'CreatedBy',      header: 'Created By',      width: 20 },
  { key: 'CreatedDate',    header: 'Created Date',    width: 16 },
  { key: 'ApprovedBy',     header: 'Approved By',     width: 20 },
  { key: 'ApprovalHours',  header: 'Approval Hours',  width: 14, numFmt: '#,##0.00' },
  { key: 'PostedBy',       header: 'Posted By',       width: 20 },
  { key: 'PostedAt',       header: 'Posted At',       width: 18 },
  { key: 'BackdatedDays',  header: 'Backdated Days',  width: 14 },
  { key: 'ClosedPeriod',   header: 'Closed Period',   width: 14 },
  { key: 'SameUser',       header: 'Same User Cr/Appr', width: 16 },
  { key: 'Source',         header: 'Source',          width: 12 },
  { key: 'Attachments',    header: 'Attachments',     width: 12 },
  { key: 'IsReversal',     header: 'Is Reversal',     width: 12 },
  { key: 'IsReversed',     header: 'Is Reversed',     width: 12 },
];

function buildExcelRows(rows: JournalAnalysisRow[]): Record<string, unknown>[] {
  return rows.map(r => ({
    JournalNo:      r.documentNumber,
    DocDate:        r.documentDate,
    PostingDate:    r.postingDate,
    Period:         r.financialPeriodCode ?? '',
    Type:           r.transactionTypeCode,
    Reference:      r.reference ?? '',
    Narration:      r.narration ?? '',
    WorkflowStatus: r.workflowStatus,
    PostingStatus:  r.postingStatus,
    Debit:          r.totalDebit,
    Credit:         r.totalCredit,
    Difference:     r.difference,
    Lines:          r.lineCount,
    CreatedBy:      r.createdByName ?? '',
    CreatedDate:    r.createdDate ?? '',
    ApprovedBy:     r.approvedByName ?? '',
    ApprovalHours:  r.approvalHours ?? '',
    PostedBy:       r.postedByName ?? '',
    PostedAt:       r.postedDate ?? '',
    BackdatedDays:  r.backdatedDays,
    ClosedPeriod:   r.isClosedPeriodPosting ? 'Yes' : 'No',
    SameUser:       r.isSameUserCreatedApproved ? 'Yes' : 'No',
    Source:         r.source,
    Attachments:    r.attachmentCount,
    IsReversal:     r.isReversal ? 'Yes' : 'No',
    IsReversed:     r.isReversed ? 'Yes' : 'No',
  }));
}

// ── Component ─────────────────────────────────────────────────────────────────

export const JournalAnalysisPage = () => {
  const { hasPermission } = usePermission();
  const { user } = useAuth();

  const [filter, setFilter] = useState<JournalAnalysisFilter>({
    fromDate: firstOfMonth(),
    toDate: today(),
    unbalancedOnly: false,
    pageNumber: 1,
    pageSize: DEFAULT_PAGE_SIZE,
  });

  /**
   * The filter actually used for the loaded page. Paging re-fetches against this
   * snapshot, so edits made in the panel after a run do not silently change the
   * criteria when the user clicks "Next".
   */
  const [applied, setApplied] = useState<JournalAnalysisFilter | null>(null);
  const [data, setData] = useState<JournalAnalysisDto | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showPrint, setShowPrint] = useState(false);
  const [activeView, setActiveView] = useState<QuickView>('ALL');
  /**
   * The backend has no reversal filter, so the "Reversed" quick view is a
   * client-side display filter over the loaded page.
   */
  const [reversedOnly, setReversedOnly] = useState(false);

  const fetchPage = useCallback(async (base: JournalAnalysisFilter, pageNumber: number) => {
    const query: JournalAnalysisFilter = { ...base, pageNumber };
    setLoading(true);
    setError(null);
    try {
      const res = await analyticsApi.getJournalAnalysis(query);
      setData(res.data?.data ?? null);
      setApplied(query);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to load report');
    } finally {
      setLoading(false);
    }
  }, []);

  const run = useCallback(() => { void fetchPage(filter, 1); }, [fetchPage, filter]);

  const goToPage = useCallback((pageNumber: number) => {
    void fetchPage(applied ?? filter, pageNumber);
  }, [fetchPage, applied, filter]);

  /** Apply a quick-view preset: reset preset-owned fields, set the new one, rerun. */
  const applyQuickView = useCallback((view: QuickView) => {
    setActiveView(view);
    const base: JournalAnalysisFilter = {
      ...filter,
      postingStatus: undefined,
      sourceFilter: undefined,
      backdatedMinDays: undefined,
      closedPeriodOnly: undefined,
      pageNumber: 1,
    };
    let next = base;
    let clientReversedOnly = false;
    switch (view) {
      case 'UNPOSTED':      next = { ...base, postingStatus: 'NotPosted' }; break;
      case 'MANUAL':        next = { ...base, sourceFilter: 'MANUAL' }; break;
      case 'AUTOMATED':     next = { ...base, sourceFilter: 'AUTOMATIC' }; break;
      case 'REVERSED':      clientReversedOnly = true; break;
      case 'BACKDATED':     next = { ...base, backdatedMinDays: 1 }; break;
      case 'CLOSED_PERIOD': next = { ...base, closedPeriodOnly: true }; break;
      case 'ALL':
      default: break;
    }
    setReversedOnly(clientReversedOnly);
    setFilter(next);
    void fetchPage(next, 1);
  }, [fetchPage, filter]);

  const applySavedView = useCallback((f: unknown) => {
    if (!f || typeof f !== 'object') return;
    setFilter(prev => ({ ...prev, ...(f as Partial<JournalAnalysisFilter>), pageNumber: 1 }));
    setActiveView('ALL');
    setReversedOnly(false);
  }, []);

  const summary = data?.summary;

  const volumeData = useMemo(
    () => (summary?.volumeByMonth ?? []).map(s => ({ label: s.label, count: s.amount })),
    [summary],
  );

  const byUserData = useMemo(
    () =>
      [...(summary?.byUser ?? [])]
        .sort((a, b) => b.amount - a.amount)
        .slice(0, TOP_N)
        .map(s => ({ label: s.label, count: s.amount })),
    [summary],
  );

  const byTypeData = useMemo(
    () =>
      [...(summary?.byTransactionType ?? [])]
        .sort((a, b) => b.amount - a.amount)
        .slice(0, TOP_N)
        .map(s => ({ label: s.label, count: s.amount })),
    [summary],
  );

  const sourceData = useMemo(
    () => [
      { name: 'Manual',    value: summary?.manual    ?? 0, fill: CHART_COLOURS[0] },
      { name: 'Automatic', value: summary?.automatic ?? 0, fill: CHART_COLOURS[1] },
    ],
    [summary],
  );

  if (!hasPermission(Permissions.RPT_JOURNAL_ANALYSIS)) {
    return (
      <div className="flex items-center justify-center h-full text-gray-500 dark:text-gray-400">
        You do not have permission to view this report.
      </div>
    );
  }

  const handleExcel = () => {
    if (!data) return;
    const page = data.journals;
    exportToExcel({
      sheetName: 'Journal Register',
      columns: EXCEL_COLUMNS,
      rows: buildExcelRows(page.items),
      title: 'Journal Register',
      subtitle: `${data.companyName} · ${data.fromDate} to ${data.toDate} · Page ${page.pageNumber} of ${page.totalPages}`,
    }, `JournalRegister_${data.fromDate}_${data.toDate}_p${page.pageNumber}`);
  };

  const handleCsv = () => {
    if (!data) return;
    const page = data.journals;
    exportToCsvRows(
      EXCEL_COLUMNS,
      buildExcelRows(page.items),
      `JournalRegister_${data.fromDate}_${data.toDate}_p${page.pageNumber}`,
    );
  };

  const items = data?.journals.items ?? [];
  const displayItems = reversedOnly
    ? items.filter(j => j.isReversal || j.isReversed)
    : items;
  const page = data?.journals;
  const filterSummary = `${filter.fromDate}..${filter.toDate} · view ${activeView}`;

  return (
    <div className="flex flex-col h-full gap-4 p-4 overflow-auto">
      {/* Page header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <BookOpen size={22} className="text-brand-500" />
          <h1 className="text-xl font-bold text-gray-900 dark:text-white">Journal Register</h1>
        </div>
        <ReportToolbar
          onExcelExport={handleExcel}
          onCsvExport={handleCsv}
          onPrint={() => setShowPrint(true)}
          hasData={!!data}
          reportKey="JOURNAL_REGISTER"
          rowCount={displayItems.length}
          filterSummary={filterSummary}
        />
      </div>

      {/* Saved views */}
      <SavedViewBar
        reportKey="JOURNAL_REGISTER"
        currentFilters={filter}
        onApply={applySavedView}
      />

      {/* Quick views */}
      <div className="flex flex-wrap items-center gap-1.5">
        <span className="text-xs font-medium text-gray-500 dark:text-gray-400 mr-1 uppercase tracking-wide">Quick views</span>
        {QUICK_VIEWS.map(v => (
          <button
            key={v.key}
            onClick={() => applyQuickView(v.key)}
            disabled={loading || !filter.fromDate || !filter.toDate}
            className={cn(
              'px-3 py-1 rounded-full text-xs font-medium border transition-colors disabled:opacity-50 disabled:cursor-not-allowed',
              activeView === v.key
                ? 'bg-brand-600 border-brand-600 text-white'
                : 'border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800',
            )}
          >
            {v.label}
          </button>
        ))}
      </div>

      {/* Filter panel */}
      <div className={cn(CARD_CLS, 'p-4 space-y-3')}>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <div>
            <label className={LABEL_CLS}>From Date *</label>
            <input
              type="date"
              value={filter.fromDate}
              onChange={e => setFilter(f => ({ ...f, fromDate: e.target.value }))}
              className={INPUT_CLS}
            />
          </div>
          <div>
            <label className={LABEL_CLS}>To Date *</label>
            <input
              type="date"
              value={filter.toDate}
              onChange={e => setFilter(f => ({ ...f, toDate: e.target.value }))}
              className={INPUT_CLS}
            />
          </div>
          <div>
            <label className={LABEL_CLS}>Workflow Status</label>
            <select
              value={filter.workflowStatus ?? ''}
              onChange={e =>
                setFilter(f => ({ ...f, workflowStatus: e.target.value || undefined }))
              }
              className={INPUT_CLS}
            >
              {WORKFLOW_STATUS_OPTIONS.map(o => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className={LABEL_CLS}>Posting Status</label>
            <select
              value={filter.postingStatus ?? ''}
              onChange={e =>
                setFilter(f => ({ ...f, postingStatus: e.target.value || undefined }))
              }
              className={INPUT_CLS}
            >
              {POSTING_STATUS_OPTIONS.map(o => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <div>
            <label className={LABEL_CLS}>Transaction Type</label>
            <input
              type="text"
              value={filter.transactionTypeCode ?? ''}
              placeholder="e.g. JV"
              onChange={e =>
                setFilter(f => ({ ...f, transactionTypeCode: e.target.value || undefined }))
              }
              className={INPUT_CLS}
            />
          </div>
          <div>
            <label className={LABEL_CLS}>Source</label>
            <select
              value={filter.sourceFilter ?? ''}
              onChange={e => {
                const v = e.target.value;
                setFilter(f => ({
                  ...f,
                  sourceFilter: v === 'MANUAL' || v === 'AUTOMATIC' ? v : undefined,
                }));
              }}
              className={INPUT_CLS}
            >
              {SOURCE_OPTIONS.map(o => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className={LABEL_CLS}>Location</label>
            <LocationSelect
              value={filter.locationId}
              onChange={id => setFilter(f => ({ ...f, locationId: id || undefined }))}
            />
          </div>
          <div>
            <label className={LABEL_CLS}>Cost Centre</label>
            <CostCentreSelect
              value={filter.costCenterId}
              onChange={id => setFilter(f => ({ ...f, costCenterId: id || undefined }))}
            />
          </div>
        </div>

        {/* Register extensions — amounts, reference, backdating */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div>
            <label className={LABEL_CLS}>Amount Min</label>
            <input
              type="number"
              min={0}
              step="0.01"
              value={filter.amountMin ?? ''}
              placeholder="0.00"
              onChange={e =>
                setFilter(f => ({ ...f, amountMin: e.target.value === '' ? undefined : Number(e.target.value) }))
              }
              className={INPUT_CLS}
            />
          </div>
          <div>
            <label className={LABEL_CLS}>Amount Max</label>
            <input
              type="number"
              min={0}
              step="0.01"
              value={filter.amountMax ?? ''}
              placeholder="No limit"
              onChange={e =>
                setFilter(f => ({ ...f, amountMax: e.target.value === '' ? undefined : Number(e.target.value) }))
              }
              className={INPUT_CLS}
            />
          </div>
          <div>
            <label className={LABEL_CLS}>Reference Contains</label>
            <input
              type="text"
              value={filter.reference ?? ''}
              placeholder="Search reference…"
              onChange={e => setFilter(f => ({ ...f, reference: e.target.value || undefined }))}
              className={INPUT_CLS}
            />
          </div>
          <div>
            <label className={LABEL_CLS}>Backdated ≥ Days</label>
            <input
              type="number"
              min={0}
              step="1"
              value={filter.backdatedMinDays ?? ''}
              placeholder="e.g. 1"
              onChange={e =>
                setFilter(f => ({ ...f, backdatedMinDays: e.target.value === '' ? undefined : Number(e.target.value) }))
              }
              className={INPUT_CLS}
            />
          </div>
        </div>

        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex flex-wrap items-center gap-4">
            <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300 cursor-pointer">
              <input
                type="checkbox"
                checked={filter.unbalancedOnly ?? false}
                onChange={e => setFilter(f => ({ ...f, unbalancedOnly: e.target.checked }))}
                className="rounded"
              />
              Unbalanced only
            </label>
            <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300 cursor-pointer">
              <input
                type="checkbox"
                checked={filter.closedPeriodOnly ?? false}
                onChange={e => setFilter(f => ({ ...f, closedPeriodOnly: e.target.checked || undefined }))}
                className="rounded"
              />
              Closed-period postings only
            </label>
          </div>
          <RunReportButton
            onClick={run}
            isLoading={loading}
            disabled={!filter.fromDate || !filter.toDate}
          />
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3 text-sm text-red-700 dark:text-red-400">
          {error}
        </div>
      )}

      {/* Loading */}
      {loading && !data && (
        <div className="flex-1 flex items-center justify-center">
          <Spinner size="lg" />
        </div>
      )}

      {/* Empty */}
      {!data && !loading && (
        <EmptyState message='Set the date range and click "Run Report" to analyse journals.' />
      )}

      {/* Results */}
      {data && summary && page && (
        <div className="flex flex-col gap-4">
          {/* Report meta */}
          <div className={cn(CARD_CLS, 'px-4 py-3')}>
            <div className="text-sm font-semibold text-gray-900 dark:text-white">{data.companyName}</div>
            <div className="text-xs text-gray-500 dark:text-gray-400">
              Journal Register — {fmtDate(data.fromDate)} to {fmtDate(data.toDate)}
              {' · '}{page.totalCount.toLocaleString('en-US')} journals
              {' · '}Generated {fmtDate(data.generatedAt)}
            </div>
          </div>

          {/* Summary tiles */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
            <SummaryTile label="Total Journals"    value={summary.totalJournals.toLocaleString('en-US')} />
            <SummaryTile label="Posted"            value={summary.posted.toLocaleString('en-US')} />
            <SummaryTile label="Unposted"          value={summary.unposted.toLocaleString('en-US')} />
            <SummaryTile label="Pending Approval"  value={summary.pendingApproval.toLocaleString('en-US')} />
            <SummaryTile label="Rejected"          value={summary.rejected.toLocaleString('en-US')} />
            <SummaryTile label="Manual"            value={summary.manual.toLocaleString('en-US')} />
            <SummaryTile label="Automatic"         value={summary.automatic.toLocaleString('en-US')} />
            <SummaryTile
              label="Unbalanced"
              value={summary.unbalanced.toLocaleString('en-US')}
              danger={summary.unbalanced > 0}
            />
            <SummaryTile
              label="Avg Approval Hours"
              value={summary.averageApprovalHours === null ? '—' : summary.averageApprovalHours.toFixed(1)}
            />
          </div>

          {/* Charts */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <Section title="Journal Volume by Month" subtitle="Journals created per month">
              {volumeData.length === 0 ? (
                <EmptyState message="No volume data for this period." />
              ) : (
                <ResponsiveContainer width="100%" height={250}>
                  <BarChart data={volumeData} margin={{ top: 8, right: 12, left: 4, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="currentColor" className="text-gray-100 dark:text-gray-800" />
                    <XAxis dataKey="label" tick={{ fontSize: 10 }} interval={0} angle={-35} textAnchor="end" height={54} />
                    <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                    <ChartTooltip content={<CountTooltip />} />
                    <Bar dataKey="count" name="Journals" fill={CHART_COLOURS[0]} radius={[3, 3, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </Section>

            <Section title="Manual vs Automatic" subtitle="Where the journals came from">
              <ResponsiveContainer width="100%" height={250}>
                <PieChart>
                  <Pie
                    data={sourceData}
                    dataKey="value"
                    nameKey="name"
                    cx="50%"
                    cy="50%"
                    innerRadius={45}
                    outerRadius={85}
                    paddingAngle={2}
                  >
                    {sourceData.map(d => <Cell key={d.name} fill={d.fill} />)}
                  </Pie>
                  <ChartTooltip content={<SourceTooltip />} />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                </PieChart>
              </ResponsiveContainer>
            </Section>

            <Section title="Journals by User" subtitle={`Top ${Math.min(TOP_N, byUserData.length)} creators`}>
              {byUserData.length === 0 ? (
                <EmptyState message="No user data for this period." />
              ) : (
                <ResponsiveContainer width="100%" height={250}>
                  <BarChart data={byUserData} margin={{ top: 8, right: 12, left: 4, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="currentColor" className="text-gray-100 dark:text-gray-800" />
                    <XAxis dataKey="label" tick={{ fontSize: 10 }} interval={0} angle={-35} textAnchor="end" height={64} />
                    <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                    <ChartTooltip content={<CountTooltip />} />
                    <Bar dataKey="count" name="Journals" fill={CHART_COLOURS[4]} radius={[3, 3, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </Section>

            <Section title="Journals by Transaction Type" subtitle={`Top ${Math.min(TOP_N, byTypeData.length)} types`}>
              {byTypeData.length === 0 ? (
                <EmptyState message="No transaction-type data for this period." />
              ) : (
                <ResponsiveContainer width="100%" height={250}>
                  <BarChart data={byTypeData} margin={{ top: 8, right: 12, left: 4, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="currentColor" className="text-gray-100 dark:text-gray-800" />
                    <XAxis dataKey="label" tick={{ fontSize: 10 }} interval={0} angle={-35} textAnchor="end" height={64} />
                    <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                    <ChartTooltip content={<CountTooltip />} />
                    <Bar dataKey="count" name="Journals" fill={CHART_COLOURS[2]} radius={[3, 3, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </Section>
          </div>

          {/* Table */}
          <div className={cn(CARD_CLS, 'flex flex-col min-h-0')}>
            <div className="flex items-center justify-between px-4 py-2.5 border-b border-gray-200 dark:border-gray-700">
              <h2 className="text-sm font-semibold text-gray-900 dark:text-white">Journals</h2>
              <div className="flex items-center gap-3">
                <span className="text-xs text-gray-500 dark:text-gray-400 tabular-nums">
                  Page {page.pageNumber} of {Math.max(page.totalPages, 1)}
                  {' · '}{page.totalCount.toLocaleString('en-US')} journals
                </span>
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => goToPage(page.pageNumber - 1)}
                    disabled={loading || page.pageNumber <= 1}
                    className="flex items-center gap-1 px-2 py-1 rounded-lg border border-gray-300 dark:border-gray-600 text-xs text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-40 disabled:cursor-not-allowed"
                    title="Previous page"
                  >
                    <ChevronLeft size={14} />
                    Prev
                  </button>
                  <button
                    onClick={() => goToPage(page.pageNumber + 1)}
                    disabled={loading || page.pageNumber >= page.totalPages}
                    className="flex items-center gap-1 px-2 py-1 rounded-lg border border-gray-300 dark:border-gray-600 text-xs text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-40 disabled:cursor-not-allowed"
                    title="Next page"
                  >
                    Next
                    <ChevronRight size={14} />
                  </button>
                </div>
              </div>
            </div>

            <div className="overflow-auto">
              <table className="w-full text-sm border-collapse min-w-[2200px]">
                <thead className="sticky top-0 bg-gray-50 dark:bg-gray-800 z-10">
                  <tr>
                    <th className={cn(TH_CLS, 'w-36')}>Journal No</th>
                    <th className={cn(TH_CLS, 'w-28')}>Doc Date</th>
                    <th className={cn(TH_CLS, 'w-28')}>Posting Date</th>
                    <th className={cn(TH_CLS, 'w-24')}>Period</th>
                    <th className={cn(TH_CLS, 'w-20')}>Type</th>
                    <th className={TH_CLS}>Reference</th>
                    <th className={cn(TH_CLS, 'w-32')}>Workflow</th>
                    <th className={cn(TH_CLS, 'w-28')}>Posting</th>
                    <th className={TH_R_CLS}>Debit</th>
                    <th className={TH_R_CLS}>Credit</th>
                    <th className={TH_R_CLS}>Difference</th>
                    <th className={TH_R_CLS}>Lines</th>
                    <th className={cn(TH_CLS, 'w-32')}>Created By</th>
                    <th className={cn(TH_CLS, 'w-28')}>Created</th>
                    <th className={cn(TH_CLS, 'w-32')}>Approved By</th>
                    <th className={TH_R_CLS}>Approval Hrs</th>
                    <th className={cn(TH_CLS, 'w-32')}>Posted By</th>
                    <th className={cn(TH_CLS, 'w-32')}>Posted At</th>
                    <th className={TH_R_CLS}>Backdated</th>
                    <th className={cn(TH_CLS, 'w-24')}>Source</th>
                    <th className={TH_R_CLS}>Attach.</th>
                    <th className={cn(TH_CLS, 'w-40')}>Flags</th>
                  </tr>
                </thead>
                <tbody>
                  {displayItems.length === 0 ? (
                    <tr>
                      <td colSpan={22} className="px-3 py-8 text-center text-sm text-gray-400 dark:text-gray-600">
                        No journals match the selected criteria.
                      </td>
                    </tr>
                  ) : (
                    displayItems.map(j => {
                      const unbalanced = j.difference !== 0;
                      return (
                        <tr
                          key={j.journalId}
                          className={cn(
                            'border-t border-gray-100 dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-800/50',
                            unbalanced && 'bg-red-50/60 dark:bg-red-900/10',
                          )}
                        >
                          <td className="px-3 py-2 text-sm font-mono text-gray-900 dark:text-gray-100 whitespace-nowrap">
                            {j.documentNumber}
                          </td>
                          <td className="px-3 py-2 text-xs text-gray-600 dark:text-gray-400 whitespace-nowrap">
                            {fmtDate(j.documentDate)}
                          </td>
                          <td className="px-3 py-2 text-xs text-gray-600 dark:text-gray-400 whitespace-nowrap">
                            {fmtDate(j.postingDate)}
                          </td>
                          <td className="px-3 py-2 text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap">
                            {j.financialPeriodCode ?? '—'}
                          </td>
                          <td className="px-3 py-2 text-xs text-gray-500 dark:text-gray-400">{j.transactionTypeCode}</td>
                          <td className="px-3 py-2 text-sm text-gray-700 dark:text-gray-300">
                            {j.reference ?? <span className="text-gray-400 dark:text-gray-600">—</span>}
                          </td>
                          <td className="px-3 py-2"><StatusBadge status={j.workflowStatus} /></td>
                          <td className="px-3 py-2"><StatusBadge status={j.postingStatus} /></td>
                          <td className={cn(AMT_CLS, 'text-gray-700 dark:text-gray-300')}>{fmtAmt(j.totalDebit)}</td>
                          <td className={cn(AMT_CLS, 'text-gray-700 dark:text-gray-300')}>{fmtAmt(j.totalCredit)}</td>
                          <td
                            className={cn(
                              AMT_CLS,
                              unbalanced
                                ? 'font-bold text-red-600 dark:text-red-400'
                                : 'text-gray-400 dark:text-gray-600',
                            )}
                            title={unbalanced ? 'Journal does not balance' : undefined}
                          >
                            {unbalanced ? fmtAmtZero(j.difference) : '—'}
                          </td>
                          <td className={cn(AMT_CLS, 'text-gray-700 dark:text-gray-300')}>{j.lineCount}</td>
                          <td className="px-3 py-2 text-xs text-gray-600 dark:text-gray-400">
                            {j.createdByName ?? '—'}
                          </td>
                          <td className="px-3 py-2 text-xs text-gray-600 dark:text-gray-400 whitespace-nowrap">
                            {fmtDate(j.createdDate)}
                          </td>
                          <td className="px-3 py-2 text-xs text-gray-600 dark:text-gray-400">
                            {j.approvedByName ?? '—'}
                          </td>
                          <td className={cn(AMT_CLS, 'text-gray-700 dark:text-gray-300')}>
                            {j.approvalHours === null ? '—' : j.approvalHours.toFixed(1)}
                          </td>
                          <td className="px-3 py-2 text-xs text-gray-600 dark:text-gray-400">
                            {j.postedByName ?? '—'}
                          </td>
                          <td className="px-3 py-2 text-xs text-gray-600 dark:text-gray-400 whitespace-nowrap">
                            {fmtDateTime(j.postedDate)}
                          </td>
                          <td
                            className={cn(
                              AMT_CLS,
                              j.backdatedDays > 0
                                ? 'font-bold text-red-600 dark:text-red-400'
                                : 'text-gray-400 dark:text-gray-600',
                            )}
                            title={j.backdatedDays > 0 ? `Entry captured ${j.backdatedDays} day(s) after the posting date` : undefined}
                          >
                            {j.backdatedDays > 0 ? `${j.backdatedDays}d` : '—'}
                          </td>
                          <td className="px-3 py-2 text-xs text-gray-500 dark:text-gray-400">{j.source}</td>
                          <td className={cn(AMT_CLS, 'text-gray-700 dark:text-gray-300')}>
                            {j.attachmentCount > 0 ? (
                              <span className="inline-flex items-center gap-1 justify-end">
                                <Paperclip size={11} className="text-gray-400" />
                                {j.attachmentCount}
                              </span>
                            ) : (
                              <span className="text-gray-400 dark:text-gray-600">—</span>
                            )}
                          </td>
                          <td className="px-3 py-2">
                            <div className="flex flex-wrap gap-1">
                              {j.isReversal && (
                                <span
                                  className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded border border-purple-200 dark:border-purple-800 bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-400 text-[10px] font-medium whitespace-nowrap"
                                  title="This journal reverses another journal"
                                >
                                  <Undo2 size={10} />
                                  Reversal
                                </span>
                              )}
                              {j.isReversed && (
                                <span
                                  className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded border border-amber-200 dark:border-amber-800 bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 text-[10px] font-medium whitespace-nowrap"
                                  title="This journal has been reversed"
                                >
                                  <RotateCcw size={10} />
                                  Reversed
                                </span>
                              )}
                              {j.isClosedPeriodPosting && (
                                <span
                                  className="inline-flex items-center px-1.5 py-0.5 rounded border border-red-200 dark:border-red-800 bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400 text-[10px] font-medium whitespace-nowrap"
                                  title="Posted into a period that was already closed at posting time"
                                >
                                  CLOSED-PERIOD
                                </span>
                              )}
                              {j.isSameUserCreatedApproved && (
                                <span
                                  className="inline-flex items-center px-1.5 py-0.5 rounded border border-amber-200 dark:border-amber-800 bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 text-[10px] font-medium whitespace-nowrap"
                                  title="Created and approved by the same user"
                                >
                                  SAME-USER
                                </span>
                              )}
                              {!j.isReversal && !j.isReversed && !j.isClosedPeriodPosting && !j.isSameUserCreatedApproved && (
                                <span className="text-xs text-gray-400 dark:text-gray-600">—</span>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>

            {/* Footer pager */}
            <div className="flex items-center justify-between px-4 py-2.5 border-t border-gray-200 dark:border-gray-700">
              <span className="text-xs text-gray-500 dark:text-gray-400 tabular-nums">
                Showing {displayItems.length.toLocaleString('en-US')} of {page.totalCount.toLocaleString('en-US')}
                {reversedOnly && ' (reversals on this page)'}
                {' · '}page size {page.pageSize}
              </span>
              <div className="flex items-center gap-1">
                <button
                  onClick={() => goToPage(page.pageNumber - 1)}
                  disabled={loading || page.pageNumber <= 1}
                  className="flex items-center gap-1 px-2 py-1 rounded-lg border border-gray-300 dark:border-gray-600 text-xs text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  <ChevronLeft size={14} />
                  Prev
                </button>
                <button
                  onClick={() => goToPage(page.pageNumber + 1)}
                  disabled={loading || page.pageNumber >= page.totalPages}
                  className="flex items-center gap-1 px-2 py-1 rounded-lg border border-gray-300 dark:border-gray-600 text-xs text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  Next
                  <ChevronRight size={14} />
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Print preview — current page only */}
      {data && page && (
        <ReportPrintPreview
          isOpen={showPrint}
          onClose={() => setShowPrint(false)}
          title="Journal Register"
          orientation="landscape"
        >
          <ReportHeader
            reportTitle="Journal Register"
            printedBy={user?.fullName}
            printedAt={new Date()}
            meta={[
              { label: 'Company',    value: data.companyName },
              { label: 'Period',     value: `${fmtDate(data.fromDate)} to ${fmtDate(data.toDate)}` },
              { label: 'Page',       value: `${page.pageNumber} of ${Math.max(page.totalPages, 1)}` },
              { label: 'Journals',   value: String(page.totalCount) },
              { label: 'Unbalanced', value: String(data.summary.unbalanced) },
            ]}
          />
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '8.5px', marginTop: '8px' }}>
            <thead>
              <tr>
                {['Journal No', 'Doc Date', 'Type', 'Reference', 'Workflow', 'Posting', 'Debit', 'Credit', 'Difference', 'Lines', 'Created By', 'Source'].map((h, i) => (
                  <th
                    key={h}
                    style={{
                      background: '#1e3a5f', color: 'white', padding: '5px 6px',
                      textAlign: i >= 6 && i <= 9 ? 'right' : 'left',
                      fontWeight: 700, whiteSpace: 'nowrap', fontSize: '8.5px',
                    }}
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {displayItems.map(j => (
                <tr key={j.journalId} style={{ borderBottom: '1px solid #f3f4f6' }}>
                  <td style={{ padding: '3px 6px', fontFamily: 'monospace' }}>{j.documentNumber}</td>
                  <td style={{ padding: '3px 6px' }}>{fmtDate(j.documentDate)}</td>
                  <td style={{ padding: '3px 6px', color: '#6b7280' }}>{j.transactionTypeCode}</td>
                  <td style={{ padding: '3px 6px' }}>{j.reference ?? ''}</td>
                  <td style={{ padding: '3px 6px' }}>{j.workflowStatus}</td>
                  <td style={{ padding: '3px 6px' }}>{j.postingStatus}</td>
                  <td style={{ padding: '3px 6px', textAlign: 'right', fontFamily: 'monospace' }}>{fmtAmt(j.totalDebit)}</td>
                  <td style={{ padding: '3px 6px', textAlign: 'right', fontFamily: 'monospace' }}>{fmtAmt(j.totalCredit)}</td>
                  <td
                    style={{
                      padding: '3px 6px', textAlign: 'right', fontFamily: 'monospace',
                      fontWeight: j.difference !== 0 ? 700 : 400,
                      color: j.difference !== 0 ? '#dc2626' : '#111827',
                    }}
                  >
                    {j.difference !== 0 ? fmtAmtZero(j.difference) : '—'}
                  </td>
                  <td style={{ padding: '3px 6px', textAlign: 'right', fontFamily: 'monospace' }}>{j.lineCount}</td>
                  <td style={{ padding: '3px 6px' }}>{j.createdByName ?? ''}</td>
                  <td style={{ padding: '3px 6px', color: '#6b7280' }}>{j.source}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <ReportFooter />
        </ReportPrintPreview>
      )}
    </div>
  );
};
